import React from 'react';

import { Button } from '@material-ui/core';
import axios from 'axios';
import type { TFunction } from 'i18next';
import type { SnackbarKey } from 'notistack';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';

type HistoryLike = {
  push: (path: string) => void;
};

export type NavigateAfterReturnToOwnerDelegationParams = {
  closeSnackbar: (key?: SnackbarKey) => void;
  enqueueSnackbar: (
    message: React.ReactNode,
    options?: {
      action?: (key: SnackbarKey) => React.ReactNode;
      onClose?: (event: unknown, reason: string | undefined, key: SnackbarKey | undefined) => void;
      persist?: boolean;
      variant?: 'default' | 'error' | 'success' | 'warning' | 'info';
    },
  ) => SnackbarKey;
  history: HistoryLike;
  t: TFunction;
  userId: number;
};

/**
 * After a delegatee returns an asset action to the owner: go to Actions dashboard if they still
 * have other delegated instances; otherwise show a completion snackbar and navigate to the home
 * dashboard (`/`) immediately (OK only closes the snackbar). Same completion path for 403 from
 * personal-actions when the user no longer qualifies.
 */
export async function navigateAfterReturnToOwnerDelegation({
  closeSnackbar,
  enqueueSnackbar,
  history,
  t,
  userId,
}: NavigateAfterReturnToOwnerDelegationParams): Promise<void> {
  const showNoMoreDelegateeActionsSnackbar = () => {
    enqueueSnackbar(t(action_library_messages.instance_actions.no_more_actions_redirect), {
      variant: 'success',
      persist: true,
      action: (snackKey: SnackbarKey) => (
        <Button
          color='inherit'
          onClick={() => {
            closeSnackbar(snackKey);
          }}
          size='small'
        >
          {t(action_library_messages.instance_actions.no_more_actions_redirect_ok)}
        </Button>
      ),
    });
    history.push(PATHS.ROOT);
  };

  try {
    const { data } = await actionLibraryInstancesApi.getPersonalActionLibraryInstances({
      delegated_to: String(userId),
      limit: 1,
      offset: 0,
    });
    if (data.count > 0) {
      history.push(PATHS.ACTION_INSTANCES_DASHBOARD);
      return;
    }
    showNoMoreDelegateeActionsSnackbar();
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      showNoMoreDelegateeActionsSnackbar();
      return;
    }
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  }
}
